import { useTranslation } from 'react-i18next';
import { Clock, Trophy } from 'lucide-react';
import { Button } from '@/ui';
import { totalOf, unitRate } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';
import { formatRemaining, type PhaseResult } from '@/lib/task-phase';
import { counterpartyName, type OfferRow } from '@/features/tasks/types';
import { BankPanel, InfoGrid, QuoteCard } from '@/features/tasks/components/QuoteCard';
import styles from './CompactOfferRow.module.css';

/**
 * TALEP DETAYINDA TEK TEKLIF — KOMPAKT SATIR (2026-10-02, M6).
 *
 * Talep basligi (tutar, cift, yon, #id, faz + sayac) sayfanin ustunde BIR KEZ duruyor; eskiden
 * her OfferCard ayni basligi tekrar ediyordu ve uc teklif ekranin tamamini dolduruyordu. Satir
 * yalniz karsilastirilan seyleri tasir: buro, BIRIM KUR, toplam ve secim.
 *
 * Kabuk QuoteCard: kazanan cercevesi (`won`) ve kaybeden solgunlugu (`lost`) oradan gelir.
 * Buro tarafi (Tekliflerim) OfferCard'i kullanmaya devam ediyor; bu satir yalniz musterinin
 * talep detayinda.
 *
 * ================================ SECIM ======================================
 *   'open'    -> "Sec" dugmesi.
 *   'waiting' -> DUGME GIBI GORUNMEYEN soluk bir metin + kalan sure ("Secim 1:23 sonra acilir").
 *                Devre disi bir dugme de basilabilir gorunur; teklif fazinda satirin secilemedigi
 *                dokunmadan anlasilmali.
 *   yok       -> hicbir sey (karar verildi, sahibi degil, sure doldu — sebep sayfa notunda).
 */

export type OfferSelection = { kind: 'open' } | { kind: 'waiting'; opensInMs: number };

export type CompactOfferRowProps = {
  offer: OfferRow;
  /** Talebin YASAYAN fazi (sayfadan). Satir kendi sayacini kurmaz. */
  phase: PhaseResult;
  /** Karara baglanmis ve KAZANAN satir: rozet + cerceve + buronun banka bilgisi (odeme icin). */
  won?: boolean;
  /** Karara baglanmis ama kazanmamis satir — soluk. */
  lost?: boolean;
  selection?: OfferSelection | null | undefined;
  onSelect?: (() => void) | undefined;
};

export function CompactOfferRow({
  offer,
  phase,
  won = false,
  lost = false,
  selection,
  onSelect,
}: CompactOfferRowProps) {
  const { t } = useTranslation();
  const pair = pairOf(offer.taskTypeCode);
  // BIRIM KUR; toplam yalniz totalOf() ile (lib/money.ts).
  const rate = unitRate(offer.offerPrice);
  const name = counterpartyName(offer.offerUserBusinessName, offer.offerUserFullName) ?? '';

  return (
    <QuoteCard phase={phase} won={won} lost={lost}>
      <div className={styles.row}>
        <div className={styles.who}>
          {won ? (
            <span className={styles.wonBadge}>
              <Trophy size={13} strokeWidth={2.2} aria-hidden="true" />
              {t('task.badges.winner')}
            </span>
          ) : null}
          <span className={styles.name}>{name}</span>
        </div>

        <div className={styles.figures}>
          <InfoGrid
            items={[
              { label: t('labels.price'), rate, currency: pair.secondCurrency, emphasis: true },
              {
                label: t('labels.total'),
                total: totalOf(rate, offer.taskAmount),
                currency: pair.secondCurrency,
              },
            ]}
          />
        </div>

        {selection?.kind === 'open' && onSelect ? (
          <Button size="sm" className={styles.select} onClick={onSelect}>
            {t('task.selection.select')}
          </Button>
        ) : selection?.kind === 'waiting' ? (
          <span className={styles.locked}>
            <Clock size={13} aria-hidden="true" />
            {t('task.selection.opensIn', { time: formatRemaining(selection.opensInMs) })}
          </span>
        ) : null}
      </div>

      {/* Odeme kazanan buronun hesabina yapilir; kaybedenlerin hesabi gosterilmez. */}
      {won ? <BankPanel account={offer.offerData?.selectedAccount} /> : null}
    </QuoteCard>
  );
}
